import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowUp, Check, X, Volume2, BookOpen, MessageSquare, AlertTriangle, Shield, BookMarked, Mic, Square, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { SourceBadge, SourceLine } from '@/components/SourceBadge';
import { formatLevel } from '@/lib/levels';
import api from '@/lib/api';

export default function WordDetail() {
  const { wordId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const isElder = user?.level >= 4;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pronunciations, setPronunciations] = useState([]);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [showAllAudio, setShowAllAudio] = useState(false);
  const [upvoted, setUpvoted] = useState({});
  const audioRef = useRef(null);

  useEffect(() => { loadWord(); }, [wordId]); // eslint-disable-line

  const loadWord = async () => {
    try {
      const res = await api.get(`/words/${wordId}`);
      setData(res.data);
      // Fetch pronunciations for this word
      if (res.data?.word?.text) {
        const audioRes = await api.get(`/audio/for/${wordId}`).catch(() => ({ data: { recordings: [] } }));
        setPronunciations(audioRes.data.recordings || []);
      }
    } catch { setData(null); }
    setLoading(false);
  };

  const playPronunciation = (audioId) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${backendUrl}/api/audio/play/${audioId}`;
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingAudio(audioId);
    audio.onended = () => setPlayingAudio(null);
    audio.onerror = () => setPlayingAudio(null);
    audio.play().catch(() => setPlayingAudio(null));
  };

  const handleUpvote = async (audioId) => {
    try {
      const res = await api.post(`/audio/${audioId}/upvote`);
      setUpvoted(prev => ({ ...prev, [audioId]: res.data.upvoted }));
      // Update the count in pronunciations
      setPronunciations(prev => prev.map(p =>
        p.audio_id === audioId ? { ...p, upvote_count: res.data.upvote_count } : p
      ));
    } catch (err) { console.error(err); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin" />
    </div>
  );

  if (!data?.word) return (
    <div className="max-w-3xl mx-auto px-4 py-12 text-center">
      <p className="text-[#57534E]">Word not found</p>
      <Link to="/search" className="text-[#4CAF50] mt-2 inline-block" onClick={(e) => { e.preventDefault(); navigate(-1); }}>Back to Translate</Link>
    </div>
  );

  const { word, translations, dict_senses, dict_examples, bible_examples, conflicts, has_conflicts, hidden_senses, hidden_examples } = data;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-safe">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-[#57534E] hover:text-[#1C1917] mb-6" data-testid="back-to-dictionary">
        <ArrowLeft className="w-4 h-4" /> Back to Translate
      </button>

      {/* Word Header */}
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 sm:p-8 mb-6" data-testid="word-header">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {word.text}
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#A8A29E]">{word.language}</span>
              {word.part_of_speech && (
                <span className="text-xs bg-[#F5F5F4] text-[#57534E] px-2 py-0.5 rounded-lg">{word.part_of_speech}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {has_conflicts && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FFF7ED] text-[#EA580C] text-xs font-semibold" data-testid="conflict-badge">
                <AlertTriangle className="w-3 h-3" /> Needs Review
              </span>
            )}
          </div>
        </div>

        {/* Pronunciation playback */}
        {pronunciations.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#E7E5E4]" data-testid="pronunciation-section">
            <p className="text-[10px] font-bold text-[#57534E] uppercase tracking-wider mb-2">Pronunciation</p>
            {/* Best/primary recording */}
            <div className="flex flex-wrap gap-2">
              {pronunciations.slice(0, 1).map(p => {
                const isPlaying = playingAudio === p.audio_id;
                const statusLabel = p.audio_status === 'approved_audio' ? 'Approved' : p.audio_status === 'elder_audio' ? 'Wirfon' : 'Community';
                const statusColor = p.audio_status === 'approved_audio' ? 'text-[#588157]' : p.audio_status === 'elder_audio' ? 'text-[#D4A373]' : 'text-[#57534E]';
                return (
                  <div key={p.audio_id} className="flex items-center gap-1.5">
                    <button onClick={() => playPronunciation(p.audio_id)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                        isPlaying ? 'bg-[#4CAF50] text-white' : 'bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]'
                      }`}
                      data-testid={`play-pronunciation-${p.audio_id}`}
                    >
                      {isPlaying ? (
                        <><Volume2 className="w-3.5 h-3.5 animate-pulse" /> Playing...</>
                      ) : (
                        <><Play className="w-3.5 h-3.5" /> <span className={statusColor}>{statusLabel}</span></>
                      )}
                      {p.is_primary && <span className="text-[8px] bg-[#588157] text-white px-1 py-0.5 rounded">Primary</span>}
                    </button>
                    {isAuthenticated && (
                      <button onClick={() => handleUpvote(p.audio_id)}
                        className={`flex items-center gap-0.5 px-2 py-1.5 rounded-lg text-[10px] transition-colors ${
                          upvoted[p.audio_id] ? 'bg-[#E8F5E9] text-[#588157]' : 'text-[#A8A29E] hover:bg-[#F5F5F4]'
                        }`}
                        data-testid={`upvote-${p.audio_id}`}
                      >
                        <ArrowUp className="w-3 h-3" />
                        {(p.upvote_count || 0) > 0 && <span>{p.upvote_count}</span>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {/* More recordings (expandable) */}
            {pronunciations.length > 1 && (
              <div className="mt-1.5">
                {!showAllAudio ? (
                  <button onClick={() => setShowAllAudio(true)}
                    className="text-[10px] text-[#4CAF50] font-semibold hover:underline"
                    data-testid="show-more-audio">
                    +{pronunciations.length - 1} more recording{pronunciations.length - 1 > 1 ? 's' : ''}
                  </button>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {pronunciations.slice(1).map(p => {
                        const isPlaying = playingAudio === p.audio_id;
                        const statusLabel = p.audio_status === 'approved_audio' ? 'Approved' : p.audio_status === 'elder_audio' ? 'Wirfon' : 'Community';
                        const statusColor = p.audio_status === 'approved_audio' ? 'text-[#588157]' : p.audio_status === 'elder_audio' ? 'text-[#D4A373]' : 'text-[#57534E]';
                        return (
                          <div key={p.audio_id} className="flex items-center gap-1.5">
                            <button onClick={() => playPronunciation(p.audio_id)}
                              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                                isPlaying ? 'bg-[#4CAF50] text-white' : 'bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]'
                              }`}
                              data-testid={`play-pronunciation-${p.audio_id}`}
                            >
                              {isPlaying ? (
                                <><Volume2 className="w-3.5 h-3.5 animate-pulse" /> Playing...</>
                              ) : (
                                <><Play className="w-3.5 h-3.5" /> <span className={statusColor}>{statusLabel}</span></>
                              )}
                              {p.is_primary && <span className="text-[8px] bg-[#588157] text-white px-1 py-0.5 rounded">Primary</span>}
                            </button>
                            {isAuthenticated && (
                              <button onClick={() => handleUpvote(p.audio_id)}
                                className={`flex items-center gap-0.5 px-2 py-1.5 rounded-lg text-[10px] transition-colors ${
                                  upvoted[p.audio_id] ? 'bg-[#E8F5E9] text-[#588157]' : 'text-[#A8A29E] hover:bg-[#F5F5F4]'
                                }`}
                                data-testid={`upvote-${p.audio_id}`}
                              >
                                <ArrowUp className="w-3 h-3" />
                                {(p.upvote_count || 0) > 0 && <span>{p.upvote_count}</span>}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <button onClick={() => setShowAllAudio(false)}
                      className="text-[10px] text-[#A8A29E] hover:underline mt-1">Show less</button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Record pronunciation option */}
        {isAuthenticated && word.language === 'lamnso' && (
          <WordPronunciationRecorder wordId={wordId} text={word.text} onRecorded={loadWord} hasPronunciations={pronunciations.length > 0} />
        )}
      </div>

      {/* 1. TRANSLATIONS (live backbone — shown first) */}
      <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 sm:p-8 mb-6" data-testid="word-translations">
        <h2 className="text-lg font-semibold text-[#1C1917] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Translations
        </h2>
        {translations.length > 0 ? (
          <div className="space-y-4">
            {translations.map((t) => (
              <TranslationCard key={t.translation_id} t={t} isAuthenticated={isAuthenticated} isElder={isElder} conflicts={conflicts} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#A8A29E]">No translations yet for this word.</p>
        )}
      </div>

      {/* 2. DICTIONARY SENSES */}
      {dict_senses?.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 sm:p-8 mb-6" data-testid="word-dict-senses">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-[#D4A373]" />
            <h2 className="text-lg font-semibold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Dictionary Senses
            </h2>
            <span className="text-xs text-[#A8A29E]">from dictionary</span>
            {isElder && hidden_senses > 0 && (
              <span className="text-[10px] text-[#A8A29E] bg-[#F5F5F4] px-2 py-0.5 rounded-full" data-testid="hidden-senses-count">
                +{hidden_senses} hidden
              </span>
            )}
          </div>
          <div className="space-y-3">
            {dict_senses.map((s, i) => (
              <div key={s.sense_id} className="flex gap-3 group" data-testid={`dict-sense-${s.sense_id}`}>
                <span className="text-sm font-bold text-[#D4A373] mt-0.5 flex-shrink-0">{s.sense_order || (i + 1)}.</span>
                <div className="flex-1">
                  <p className="text-sm text-[#1C1917] leading-relaxed">{s.sense}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {s.pos && <span className="text-[10px] bg-[#F5F5F4] text-[#57534E] px-1.5 py-0.5 rounded">{s.pos}</span>}
                    <SourceLine source={s.source} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. EXAMPLE SENTENCES */}
      {dict_examples?.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 sm:p-8" data-testid="word-dict-examples">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-[#588157]" />
            <h2 className="text-lg font-semibold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Example Sentences
            </h2>
            {isElder && hidden_examples > 0 && (
              <span className="text-[10px] text-[#A8A29E] bg-[#F5F5F4] px-2 py-0.5 rounded-full" data-testid="hidden-examples-count">
                +{hidden_examples} hidden
              </span>
            )}
          </div>
          <div className="space-y-3">
            {dict_examples.map((ex) => (
              <div key={ex.example_id} className="border-l-2 border-[#588157]/30 pl-4 py-1" data-testid={`dict-example-${ex.example_id}`}>
                <p className="text-sm font-medium text-[#1C1917]">{ex.lamnso}</p>
                {ex.english && (
                  <p className="text-sm text-[#57534E] mt-0.5 italic">{ex.english}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 4. LITERATURE EXAMPLES */}
      {bible_examples?.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 sm:p-8 mt-6" data-testid="word-literature-examples">
          <div className="flex items-center gap-2 mb-4">
            <BookMarked className="w-5 h-5 text-[#78716C]" />
            <h2 className="text-lg font-semibold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Used in Literature
            </h2>
            <SourceBadge source="bible" variant="tag" />
          </div>
          <div className="space-y-3">
            {bible_examples.map((v) => (
              <div key={v.verse_id} className="border-l-2 border-[#78716C]/30 pl-4 py-1" data-testid={`lit-ex-${v.verse_id}`}>
                <p className="text-sm text-[#1C1917] leading-relaxed">{v.lamnso}</p>
                {v.english && (
                  <p className="text-xs text-[#57534E] mt-0.5 italic">{v.english}</p>
                )}
                <span className="text-[10px] text-[#A8A29E] mt-1 inline-block">
                  {v.book_name_en} {v.chapter}:{v.verse}{v.verse_end ? `-${v.verse_end}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function TranslationCard({ t, isAuthenticated, isElder, conflicts }) {
  const hasConflict = t.conflict_flag;
  const relatedConflicts = conflicts?.filter(c => c.translation_id === t.translation_id) || [];

  const pct = Math.round((t.confidence_score || 0) * 100);
  const barColor = pct >= 90 ? 'bg-[#588157]' : pct >= 70 ? 'bg-[#D4A373]' : 'bg-[#4CAF50]';
  const statusStyles = {
    confirmed: 'bg-[#EAF1EA] text-[#588157]',
    candidate: 'bg-[#F5F5F4] text-[#57534E]',
    rejected: 'bg-[#E8F5E9] text-[#4CAF50]'
  };

  return (
    <div className={`border rounded-xl p-4 ${hasConflict ? 'border-[#EA580C]/40 bg-[#FFF7ED]/30' : 'border-[#E7E5E4]'}`}
      data-testid={`translation-${t.translation_id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-[#1C1917]">{t.translated_word?.text || t.english_text || t.lamnso_text || '?'}</span>
        <div className="flex items-center gap-2">
          {hasConflict && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FFF7ED] text-[#EA580C] flex items-center gap-0.5"
              data-testid={`conflict-flag-${t.translation_id}`}>
              <AlertTriangle className="w-2.5 h-2.5" />
              {isElder ? 'Meaning conflict' : 'Needs review'}
            </span>
          )}
          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusStyles[t.status] || statusStyles.candidate}`}>
            {t.status}
          </span>
        </div>
      </div>

      {/* Confidence bar */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 h-1.5 bg-[#F5F5F4] rounded-full overflow-hidden">
          <div className={`h-full ${barColor} rounded-full`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs font-semibold text-[#57534E]">{pct}%</span>
      </div>

      <div className="flex items-center gap-3 text-xs text-[#A8A29E]">
        <SourceBadge source={t.source} />
        {t.linked_senses?.length > 0 && (
          <span className="text-[#D4A373]">{t.linked_senses.length} linked sense{t.linked_senses.length > 1 ? 's' : ''}</span>
        )}
      </div>

      {/* Linked sense evidence (Elder/Admin view) */}
      {isElder && t.linked_senses?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#E7E5E4]">
          <p className="text-[10px] font-bold text-[#D4A373] uppercase tracking-wider mb-1">Linked Evidence</p>
          {t.linked_senses.map((ls, i) => (
            <div key={ls.link_id || i} className="flex items-start gap-2 py-1">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${
                ls.alignment === 'match' ? 'bg-[#EAF1EA] text-[#588157]' :
                ls.alignment === 'near_match' ? 'bg-[#FDF6F0] text-[#D4A373]' :
                ls.alignment === 'conflict' ? 'bg-[#E8F5E9] text-[#4CAF50]' :
                'bg-[#F5F5F4] text-[#57534E]'
              }`}>{ls.alignment}</span>
              <p className="text-[10px] text-[#57534E] leading-relaxed">{ls.sense_text}</p>
            </div>
          ))}
        </div>
      )}

      {/* Conflict details for Elders */}
      {isElder && relatedConflicts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#EA580C]/20">
          <p className="text-[10px] font-bold text-[#EA580C] mb-1">Pending Conflicts</p>
          {relatedConflicts.map(c => (
            <div key={c.conflict_id} className="text-[10px] text-[#57534E] py-1">
              <p>Dictionary says: <em className="text-[#1C1917]">"{c.dictionary_sense?.slice(0, 120)}..."</em></p>
              <Link to={`/validate?focus=${c.translation_id}`} className="text-[#4CAF50] font-semibold hover:underline mt-0.5 inline-block"
                data-testid={`resolve-conflict-${c.conflict_id}`}>
                Review in Elder Queue
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function WordPronunciationRecorder({ wordId, text, onRecorded, hasPronunciations }) {
  const [showRecorder, setShowRecorder] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const { recording, audioBlob, audioUrl, error, startRecording, stopRecording, reset: resetRecorder, isSupported } = require('@/hooks/useAudioRecorder').useAudioRecorder();

  const upload = async () => {
    if (!audioBlob) return;
    setUploading(true);
    try {
      const formData = new FormData();
      const ext = audioBlob.type?.includes('mp4') ? 'mp4' : audioBlob.type?.includes('ogg') ? 'ogg' : 'webm';
      formData.append('file', audioBlob, `pronunciation.${ext}`);
      await api.post(`/audio/pronunciation?entry_id=${encodeURIComponent(wordId)}&text=${encodeURIComponent(text)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploaded(true);
      setShowRecorder(false);
      onRecorded?.();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const handleReset = () => {
    resetRecorder();
    setUploaded(false);
  };

  if (!isSupported) return null;

  if (uploaded) {
    return (
      <div className="mt-3 pt-3 border-t border-[#E7E5E4] flex items-center gap-2 text-xs text-[#588157]" data-testid="pronunciation-uploaded">
        <Check className="w-3.5 h-3.5" /> Pronunciation saved! <button onClick={() => { handleReset(); setShowRecorder(true); }} className="text-[#A8A29E] underline">Record another</button>
      </div>
    );
  }

  if (!showRecorder) {
    return (
      <div className={`mt-3 ${hasPronunciations ? '' : 'pt-3 border-t border-[#E7E5E4]'}`}>
        <button onClick={() => setShowRecorder(true)}
          className="flex items-center gap-1.5 text-xs text-[#57534E] hover:text-[#4CAF50] transition-colors"
          data-testid="show-recorder-btn">
          <Mic className="w-3.5 h-3.5" />
          {hasPronunciations ? 'Add your pronunciation' : 'Record pronunciation'}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-[#E7E5E4]" data-testid="word-pronunciation-recorder">
      <p className="text-[10px] font-bold text-[#57534E] uppercase tracking-wider mb-2">Add Pronunciation</p>

      {error && (
        <p className="text-[10px] text-red-600 mb-2 bg-red-50 px-3 py-1.5 rounded-lg" data-testid="recording-error">{error}</p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {!audioBlob ? (
          <>
            {isSupported && (
              <button onClick={recording ? stopRecording : startRecording}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  recording ? 'bg-[#4CAF50] text-white animate-pulse' : 'bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9]'
                }`}
                data-testid="record-btn">
                {recording ? <><Square className="w-3.5 h-3.5" /> Stop Recording</> : <><Mic className="w-3.5 h-3.5" /> Record</>}
              </button>
            )}
            <label className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4] cursor-pointer transition-colors">
              <Play className="w-3.5 h-3.5" /> Upload Audio
              <input type="file" accept="audio/*" className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  try {
                    const formData = new FormData();
                    formData.append('file', file);
                    await api.post(`/audio/pronunciation?entry_id=${encodeURIComponent(wordId)}&text=${encodeURIComponent(text)}`, formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    setUploaded(true);
                    setShowRecorder(false);
                    onRecorded?.();
                  } catch (err) {
                    alert('Upload failed: ' + (err.response?.data?.detail || err.message));
                  }
                  setUploading(false);
                  e.target.value = '';
                }}
                data-testid="upload-audio-input" />
            </label>
          </>
        ) : (
          <>
            <audio src={audioUrl} controls className="h-8" style={{ maxWidth: '180px' }} />
            <Button size="sm" onClick={upload} disabled={uploading}
              className="bg-[#588157] hover:bg-[#436442] text-white rounded-xl text-xs gap-1" data-testid="save-pronunciation-btn">
              {uploading ? 'Saving...' : 'Save'}
            </Button>
            <button onClick={handleReset} className="text-[10px] text-[#A8A29E] hover:underline">Discard</button>
          </>
        )}
        <button onClick={() => { handleReset(); setShowRecorder(false); }} className="text-[10px] text-[#A8A29E] hover:underline">Cancel</button>
      </div>
    </div>
  );
}
